import { themeBorderRadius } from '$lib/Stores/stores';

import { get } from 'svelte/store';

import type {
    BorderRadius,
    BorderRadiusConfiguration,
    BorderRadiusScale,
} from '$lib/types/Components/SharedTypes';
import type { BrandBorderRadius } from '$lib/types/Theme';

// Legacy (pre-Theming 2.0) token → class map.
// Preserved byte-for-byte from the original implementation
// so funnels not in Theming 2.0 render identically.
const legacyBorderRadius = {
    A: {
        sharp: 'fun-rounded',
        small: 'fun-rounded',
        medium: 'fun-rounded-lg',
        large: 'fun-rounded-lg',
    },
    B: {
        sharp: 'fun-rounded',
        small: 'fun-rounded-lg',
        medium: 'fun-rounded-xl',
        large: 'fun-rounded-full',
    },
    C: {
        sharp: 'fun-rounded',
        small: 'fun-rounded-lg',
        medium: 'fun-rounded-xl',
        large: 'fun-rounded-2xl',
    },
    D: {
        sharp: 'fun-rounded',
        small: 'fun-rounded-xl',
        medium: 'fun-rounded-2xl',
        large: 'fun-rounded-[20px]',
    },
    E: {
        sharp: 'fun-rounded',
        small: 'fun-rounded-2xl',
        medium: 'fun-rounded-[20px]',
        large: 'fun-rounded-3xl',
    },
    // F: legacy compat shim — new scale, no pre-V2 components use it.
    // Values mirror E so any stray legacy payload routed here still renders.
    F: {
        sharp: 'fun-rounded',
        small: 'fun-rounded-2xl',
        medium: 'fun-rounded-[20px]',
        large: 'fun-rounded-3xl',
    },
    // G: legacy compat shim for videoQuestionFrame (was E, is G in V2).
    // Values copied from E so old publishes keep rendering the same class.
    G: {
        sharp: 'fun-rounded',
        small: 'fun-rounded-2xl',
        medium: 'fun-rounded-[20px]',
        large: 'fun-rounded-3xl',
    },
} as const satisfies BorderRadiusConfiguration;

// Theming 2.0 token → class map. Mirrors
// perspective-app-next/modules/app/brands/helpers/resolveStyleClasses.ts
// with the funnel-app `fun-` Tailwind prefix applied.
// SYNC: keep in lock-step with resolveStyleClasses.ts in perspective-app-next.
const borderRadius = {
    // A — Small/subtle (List Image, Dropdown Item, DatePicker Day)
    A: {
        none: 'fun-rounded-none',
        sm: 'fun-rounded-md',
        md: 'fun-rounded-lg',
        lg: 'fun-rounded-xl',
        xl: 'fun-rounded-2xl',
        '2xl': 'fun-rounded-3xl',
        full: 'fun-rounded-full',
    },
    // B — Button baseline (Button, Header Button, Submit) — maps 1:1 to tokens
    B: {
        none: 'fun-rounded-none',
        sm: 'fun-rounded-sm',
        md: 'fun-rounded-md',
        lg: 'fun-rounded-lg',
        xl: 'fun-rounded-xl',
        '2xl': 'fun-rounded-2xl',
        full: 'fun-rounded-full',
    },
    // C — Card (Accordion, Choice, Fields Wrapper, Dropdown, Inputs)
    C: {
        none: 'fun-rounded-none',
        sm: 'fun-rounded-lg',
        md: 'fun-rounded-xl',
        lg: 'fun-rounded-2xl',
        xl: 'fun-rounded-3xl',
        '2xl': 'fun-rounded-[28px]',
        full: 'fun-rounded-full',
    },
    // D — Frame (Grid Column, Image, Video, Slider, Embed)
    D: {
        none: 'fun-rounded-none',
        sm: 'fun-rounded-xl',
        md: 'fun-rounded-2xl',
        lg: 'fun-rounded-[20px]',
        xl: 'fun-rounded-3xl',
        '2xl': 'fun-rounded-[28px]',
        full: 'fun-rounded-full',
    },
    // E — Top corners (Media answers, top half)
    E: {
        none: 'fun-rounded-t-none',
        sm: 'fun-rounded-t-lg',
        md: 'fun-rounded-t-xl',
        lg: 'fun-rounded-t-2xl',
        xl: 'fun-rounded-t-3xl',
        '2xl': 'fun-rounded-t-[28px]',
        full: 'fun-rounded-t-full',
    },
    // F — Bottom corners (Media answers, bottom half)
    F: {
        none: 'fun-rounded-b-none',
        sm: 'fun-rounded-b-lg',
        md: 'fun-rounded-b-xl',
        lg: 'fun-rounded-b-2xl',
        xl: 'fun-rounded-b-3xl',
        '2xl': 'fun-rounded-b-[28px]',
        full: 'fun-rounded-b-full',
    },
    // G — Webinar / Video Question frame
    G: {
        none: 'fun-rounded-none',
        sm: 'fun-rounded-2xl',
        md: 'fun-rounded-[20px]',
        lg: 'fun-rounded-3xl',
        xl: 'fun-rounded-[28px]',
        '2xl': 'fun-rounded-[32px]',
        full: 'fun-rounded-t-full',
    },
} as const satisfies Record<BorderRadiusScale, Record<BrandBorderRadius, string>>;

const componentBorderRadius = {
    blockFrame: 'D',
    image: 'D',
    video: 'D',
    button: 'B',
    listImage: 'A',
    faqItem: 'C',
    sliderImage: 'D',
    embed: 'D',
    choiceItem: 'C',
    fieldsWrapper: 'C',
    dropdown: 'C',
    menu: 'C',
    dropdownItem: 'A',
    datePickerDay: 'A',
    videoQuestionFrame: 'G',
    mediaAnswerTop: 'E',
    mediaAnswerBottom: 'F',
    webinarFrame: 'G',
} as const satisfies Record<string, BorderRadiusScale>;

const TOKENS: ReadonlySet<string> = new Set<BrandBorderRadius>([
    'none',
    'sm',
    'md',
    'lg',
    'xl',
    '2xl',
    'full',
]);

const isV2Token = (value: string): value is BrandBorderRadius => TOKENS.has(value);

export const getBorderRadius = (
    component: keyof typeof componentBorderRadius,
    themeSetting?: BorderRadius | BrandBorderRadius,
): string => {
    const value = themeSetting ?? (get(themeBorderRadius) as BorderRadius | BrandBorderRadius);
    const scale = componentBorderRadius[component];

    const resolved = isV2Token(value)
        ? borderRadius[scale][value]
        : legacyBorderRadius[scale][value as BorderRadius];

    return resolved ?? 'fun-rounded-md';
};
